import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Check, X, Pencil, Trash2 } from "lucide-react";

export const ManageCategoriesDialog = ({ open, onOpenChange, categories, onChanged }) => {
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post("/admin/categories", { name });
      setNewName("");
      toast.success("Category added");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startEdit = (c) => { setEditId(c.id); setEditName(c.name); };

  const saveEdit = async () => {
    const name = editName.trim();
    if (!name) return;
    try {
      await api.put(`/admin/categories/${editId}`, { name });
      setEditId(null);
      toast.success("Category renamed (products updated)");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? Products in it will become uncategorised.`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Category deleted");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="manage-categories-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Product categories</DialogTitle>
          <DialogDescription>Add, rename or delete categories. Renaming updates all products in that category.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="New category name"
              className="rounded-sm"
              data-testid="new-category-input"
            />
            <Button onClick={add} className="bg-[#002fa7] hover:bg-[#00227b] rounded-sm" data-testid="add-category-btn">
              <Plus className="w-4 h-4 mr-1" strokeWidth={1.5}/> Add
            </Button>
          </div>
          <div className="border border-gray-200 divide-y divide-gray-100 max-h-72 overflow-y-auto">
            {categories.length === 0 && <div className="p-4 text-sm text-gray-400">No categories yet.</div>}
            {categories.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`category-row-${c.id}`}>
                {editId === c.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit()} className="rounded-sm h-8" data-testid="edit-category-input"/>
                    <button onClick={saveEdit} className="text-green-600 hover:text-green-700" data-testid={`save-category-${c.id}`}><Check className="w-4 h-4"/></button>
                    <button onClick={() => setEditId(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4"/></button>
                  </div>
                ) : (
                  <>
                    <span className="font-medium text-gray-800">{c.name}</span>
                    <div className="flex gap-3">
                      <button onClick={() => startEdit(c)} className="text-gray-400 hover:text-[#002fa7]" data-testid={`edit-category-${c.id}`}><Pencil className="w-4 h-4" strokeWidth={1.5}/></button>
                      <button onClick={() => remove(c)} className="text-gray-400 hover:text-red-600" data-testid={`delete-category-${c.id}`}><Trash2 className="w-4 h-4" strokeWidth={1.5}/></button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
